import { Patient } from "../../../services/patient.service";
import { lastConsultationDate } from "./patient-format";

const normalize = (value: string | null | undefined) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export type PatientSortOrder =
  | "name-asc"
  | "name-desc"
  | "consultation-desc"
  | "consultation-asc"
  | "return-desc"
  | "return-asc";

export const PATIENT_SORT_OPTIONS: Array<{ value: PatientSortOrder; label: string }> = [
  { value: "name-asc", label: "Nome A-Z" },
  { value: "name-desc", label: "Nome Z-A" },
  { value: "consultation-desc", label: "Consulta mais recente" },
  { value: "consultation-asc", label: "Consulta mais antiga" },
  { value: "return-desc", label: "Retorno mais recente" },
  { value: "return-asc", label: "Retorno mais antigo" },
];

const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

const toDateMs = (value?: string): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const compareNullableDate = (left: number | null, right: number | null, direction: "asc" | "desc") => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return direction === "asc" ? left - right : right - left;
};

export function filterPatients(patients: Patient[], query: string): Patient[] {
  const q = normalize(query.trim());
  if (!q) return patients;
  return patients.filter((patient) => normalize(patient.name).includes(q) || (patient.tags ?? []).some((tag) => normalize(tag).includes(q)));
}

export function sortPatients(patients: Patient[], sortOrder: PatientSortOrder): Patient[] {
  return [...patients].sort((left, right) => {
    if (sortOrder === "name-asc" || sortOrder === "name-desc") {
      const direction = sortOrder === "name-asc" ? 1 : -1;
      return direction * collator.compare(left.name, right.name);
    }

    if (sortOrder === "consultation-asc" || sortOrder === "consultation-desc") {
      const direction = sortOrder === "consultation-asc" ? "asc" : "desc";
      const compare = compareNullableDate(toDateMs(lastConsultationDate(left)), toDateMs(lastConsultationDate(right)), direction);
      if (compare !== 0) return compare;
      return collator.compare(left.name, right.name);
    }

    const direction = sortOrder === "return-asc" ? "asc" : "desc";
    const compare = compareNullableDate(toDateMs(left.returnDate), toDateMs(right.returnDate), direction);
    if (compare !== 0) return compare;
    return collator.compare(left.name, right.name);
  });
}
